import { useState } from 'react';
import { Link } from 'react-router';
import {
  getOngoingEvents,
  getUpcomingEvents,
  getCompletedEvents,
} from '../../lib/events.js';
import { getDisciplineBySlug } from '../../lib/disciplines.js';
import { formatDate } from '../../lib/dateUtils.js';
import { useDocumentTitle } from '../../lib/useDocumentTitle.js';
import styles from './EventsPage.module.css';

function EventCard({ event, statusType }) {
  const discipline = getDisciplineBySlug(event.discipline);

  const getBadge = () => {
    if (statusType === 'ongoing') {
      return (
        <span className={styles.badgeLive}>
          <span className="live-pulse-dot" /> Ongoing
        </span>
      );
    }
    if (statusType === 'upcoming') {
      return <span className={styles.badgeUpcoming}>Upcoming</span>;
    }
    return <span className={styles.badgeCompleted}>Completed</span>;
  };

  return (
    <article className={styles.card}>
      <div className={styles.cardTop}>
        {discipline && (
          <span className={styles.disciplineBadge}>{discipline.shortName}</span>
        )}
        {getBadge()}
      </div>

      <h3 className={styles.cardHeading}>
        <Link to={`/events/${event.slug}`}>{event.title}</Link>
      </h3>

      <div className={`meta ${styles.cardMeta}`}>
        <span>{formatDate(event.date)}</span>
        {event.endDate && event.endDate !== event.date && (
          <span>to {formatDate(event.endDate)}</span>
        )}
        {event.startTime && <span>· {event.startTime}</span>}
      </div>

      <p className={styles.cardDescription}>{event.description}</p>

      <div className={styles.metadataGrid}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Venue:</span>
          <span>{event.room || 'TBA'}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Audience:</span>
          <span>{event.audience || 'Open to all students'}</span>
        </div>
        {event.requirements && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Prereqs:</span>
            <span>{event.requirements}</span>
          </div>
        )}
      </div>

      <div className={styles.cardActions}>
        <Link to={`/events/${event.slug}`} className={styles.btnPrimary}>
          {event.hasMarkdown ? 'View Details and Resources →' : 'Event Overview →'}
        </Link>
        {event.registrationUrl && statusType !== 'completed' && (
          <a
            href={event.registrationUrl}
            target="_blank"
            rel="noreferrer"
            className={styles.btnSecondary}
          >
            Register ↗
          </a>
        )}
      </div>
    </article>
  );
}

export default function EventsPage() {
  useDocumentTitle('Events');
  const [activeTab, setActiveTab] = useState('all');

  const ongoing = getOngoingEvents();
  const upcoming = getUpcomingEvents();
  const completed = getCompletedEvents();

  return (
    <div className="container" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
      <header className={styles.pageHeader}>
        <h1 className={styles.title}>Events and Workshops</h1>
        <p className={styles.lead}>
          The official technical calendar of Yet Another Coding Club. Hackathons, competitive
          programming contests, system design bootcamps, and game jams at IIT Palakkad.
        </p>

        {/* Category Tabs */}
        <div className={styles.tabsBar}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'all' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Events{' '}
            <span className={styles.tabCount}>
              {ongoing.length + upcoming.length + completed.length}
            </span>
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'ongoing' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('ongoing')}
          >
            Ongoing <span className={styles.tabCount}>{ongoing.length}</span>
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'upcoming' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            Upcoming <span className={styles.tabCount}>{upcoming.length}</span>
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'completed' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            Completed & Archives <span className={styles.tabCount}>{completed.length}</span>
          </button>
        </div>
      </header>

      {/* Ongoing Events */}
      {(activeTab === 'all' || activeTab === 'ongoing') && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className="live-pulse-dot" />
            <h2 className={styles.sectionTitle}>Ongoing Events</h2>
          </div>
          {ongoing.length === 0 ? (
            <div className={styles.emptyNotice}>
              <p>No active sessions or jams running at this moment. Check upcoming events below.</p>
            </div>
          ) : (
            <div className={styles.grid}>
              {ongoing.map((event) => (
                <EventCard key={event.slug} event={event} statusType="ongoing" />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Upcoming Events */}
      {(activeTab === 'all' || activeTab === 'upcoming') && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Upcoming Events</h2>
          </div>
          {upcoming.length === 0 ? (
            <div className={styles.emptyNotice}>
              <p>New events are currently being scheduled. Check back soon.</p>
            </div>
          ) : (
            <div className={styles.grid}>
              {upcoming.map((event) => (
                <EventCard key={event.slug} event={event} statusType="upcoming" />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Completed Events */}
      {(activeTab === 'all' || activeTab === 'completed') && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Completed Events and Archive</h2>
          </div>
          {completed.length === 0 ? (
            <div className={styles.emptyNotice}>
              <p>No past event archives recorded yet.</p>
            </div>
          ) : (
            <div className={styles.grid}>
              {completed.map((event) => (
                <EventCard key={event.slug} event={event} statusType="completed" />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
